import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button } from '@/components/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/shadcn-ui/card';
import { Input } from '@/components/shadcn-ui/input';
import { Label } from '@/components/shadcn-ui/label';
import { auth } from '@/services/auth';
import { logError } from '@/services/logger';

type RecoveryState = 'unknown' | 'allowed' | 'disallowed';

function isRecoveryUrl(): boolean {
  return window.location.hash.includes('type=recovery');
}

export function ResetPasswordScene() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoveryState, setRecoveryState] = useState<RecoveryState>('unknown');
  const navigate = useNavigate();

  useEffect(() => {
    const { data } = auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryState('allowed');
      }
    });

    void (async () => {
      try {
        const { data: sessionData } = await auth.getSession();
        if (sessionData.session && isRecoveryUrl()) {
          setRecoveryState('allowed');
        } else if (!sessionData.session) {
          setRecoveryState('disallowed');
        }
      } catch (error) {
        logError(error, 'Failed to get session');
        setRecoveryState('disallowed');
      }
    })();

    return () => {
      data.subscription.unsubscribe();
    };
  }, []);

  function handleSubmit(e: React.SubmitEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    void (async () => {
      try {
        const { error: updateError } = await auth.updatePassword(newPassword);

        if (updateError) {
          setError('Failed to update password. Please try again.');
          setIsLoading(false);
          return;
        }

        void navigate('/sign-in');
      } catch (error) {
        logError(error, 'Failed to update password');
        setError('Failed to update password. Please try again.');
        setIsLoading(false);
      }
    })();
  }

  if (recoveryState === 'unknown') {
    return (
      <div className="container flex h-screen w-screen flex-col items-center justify-center">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
          <Card>
            <CardHeader>
              <CardTitle>Reset Password</CardTitle>
            </CardHeader>
            <CardContent>
              <div>Loading...</div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (recoveryState === 'disallowed') {
    return (
      <div className="container flex h-screen w-screen flex-col items-center justify-center">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
          <Card>
            <CardHeader>
              <CardTitle>Reset Password</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <div className="relative rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
                  This password reset link is invalid or has expired.
                </div>
                <div className="mt-4 text-center text-sm">
                  <Link
                    to="/forgot-password"
                    className="underline underline-offset-4"
                  >
                    Request a new password reset link
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container flex h-screen w-screen flex-col items-center justify-center">
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
        <Card>
          <CardHeader>
            <CardTitle>Reset Password</CardTitle>
            <CardDescription>Enter your new password below</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col gap-6">
                {error && (
                  <div className="relative rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
                    {error}
                  </div>
                )}
                <div className="grid gap-3">
                  <Label htmlFor="password">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                    }}
                    required
                  />
                </div>
                <div className="grid gap-3">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                    }}
                    required
                  />
                </div>
                <div className="flex flex-col gap-3">
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? 'Updating...' : 'Reset Password'}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
